import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Badge from '@atlaskit/badge';
import Heading from '@atlaskit/heading';
import Popup from '@atlaskit/popup';
import { Card, CardAction, TitleBlock } from '@atlaskit/smart-card';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { CompassComponentLink } from '../../../../../common/services/search-compass-components/types.tsx';
import messages from '../messages.tsx';

export type Props = {
	repositories: CompassComponentLink[];
};

export const MAX_DISPLAYED_AMOUNT = 99;

const RepositoriesPopup = ({ repositories }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	if (repositories.length === 0) {
		return null;
	}

	const list = repositories.slice(0, MAX_DISPLAYED_AMOUNT);

	const content = () => (
		<ContentWrapper>
			<HeadingWrapper>
				<Heading size="xxsmall" as="div">
					{formatMessage(messages.repositories)}
				</Heading>
			</HeadingWrapper>

			{list.map((link) => (
				<Row key={`repository-link[${link.id}]`}>
					<CardContainer>
						<Card
							testId="project-directories.components.compass-table.cells.repository.repository-links-popup.smart-link"
							url={link.url}
							appearance="inline"
							placeholder={link.name || link.url}
							ui={{
								hideBackground: true,
								hideElevation: true,
								hidePadding: true,
								clickableContainer: true,
							}}
							onClick={() => {
								const analyticsEvent = createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'link',
								});

								fireUIAnalytics(analyticsEvent, 'repositoryLink');
							}}
							actionOptions={{
								hide: false,
								exclude: [CardAction.ChangeStatusAction],
							}}
						>
							<TitleBlock text={link.name} maxLines={1} />
						</Card>
					</CardContainer>
				</Row>
			))}
		</ContentWrapper>
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="left-start"
			content={content}
			trigger={(triggerProps) => (
				<BadgeContainer
					{...triggerProps}
					onClick={() => {
						setIsOpen(!isOpen);

						const analyticsEvent = createAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'button',
						});

						fireUIAnalytics(analyticsEvent, 'repositoryLinkPopup');
					}}
				>
					<Badge
						testId="project-directories.components.compass-table.cells.repository.repository-links-popup.badge-button"
						appearance={!isOpen ? undefined : 'primaryInverted'}
					>
						+{list.length}
					</Badge>
				</BadgeContainer>
			)}
		/>
	);
};

export default RepositoriesPopup;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BadgeContainer = styled.span({
	cursor: 'pointer',
	margin: `${token('space.050', '4px')} 0px ${token('space.050', '4px')} ${token(
		'space.050',
		'4px',
	)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover > span': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: `${token('color.text', colors.N800)} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: `${token('color.background.neutral.hovered', colors.N90)} !important`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	width: 222,
	height: '100%',
	padding: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	marginBottom: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text', colors.N900),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardContainer = styled.div({
	padding: `0 ${token('space.150', '12px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Row = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: 222,
	height: 40,
	marginBottom: token('space.100', '8px'),
	borderRadius: 3,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.background.accent.gray.subtlest', colors.N30),
});
